import { datadogRum } from "@datadog/browser-rum";

// constants
import { DD_RUM_AXEPTIO_KEY } from "@/constants/datadog.constants.ts";
import { HEAP_AXEPTIO_KEY } from "@/constants/heap.constants";
import { initHeap } from "@/helpers/heap";
import { JWT_EXPIRED_ERROR_MESSAGE } from "./constants/jwt.constants";
import { getAxeptioVendorCookie } from "./helpers/getAxpetioVendorCookie";

// Current heap & datadog permissions from cookies
const { isVendorActive: isHeapActive } =
  getAxeptioVendorCookie(HEAP_AXEPTIO_KEY);
const { isVendorActive: isDatadogActive } =
  getAxeptioVendorCookie(DD_RUM_AXEPTIO_KEY);

type VendorPermissions = {
  isHeapActive: boolean;
  isDatadogActive: boolean;
};

const currentVendorPermissions: VendorPermissions = {
  isHeapActive,
  isDatadogActive,
};

export const setAxeptioSettings = (lang: string) => {
  const axeptioClientId = import.meta.env.VITE_AXEPTIO_CLIENT_ID;
  if (!axeptioClientId || !window.axeptioSDK) {
    return;
  }

  const userLang = lang || navigator.language.slice(0, 2);
  const axceptiocookieVersion: Record<string, string> = {
    en: "_en-EU",
    fr: "",
    it: "_it",
    de: "_de",
    es: "_es",
    gr: "_gr",
    pl: "_pl",
    hr: "_hr",
    sl: "_sl",
  };

  const cookiesVersion = `marketplace${axceptiocookieVersion[userLang] || ""}`;

  // Wrap in try/catch to handle race condition where SDK exists but client isn't ready
  try {
    window.axeptioSDK.setCookiesVersion(cookiesVersion);
  } catch {
    // SDK not fully initialized yet, retry after a short delay
    setTimeout(() => {
      try {
        window.axeptioSDK?.setCookiesVersion(cookiesVersion);
      } catch {
        console.warn("Axeptio: Failed to set cookies version after retry");
      }
    }, 1000);
  }
};

const initializeOrUpdateDatadog = (): void => {
  datadogRum.init({
    applicationId: import.meta.env.VITE_DD_APP_ID,
    clientToken: import.meta.env.VITE_DD_CLIENT_TOKEN,
    site: "datadoghq.eu",
    service: "kazaar-app",
    env: import.meta.env.VITE_DD_ENV,
    allowedTracingUrls: [/^https:\/\/[^/]+\.kazaar\.com/],
    // Specify a version number to identify the deployed version of your application in Datadog
    // version: '1.0.0',
    sessionSampleRate: 100,
    sessionReplaySampleRate: 100,
    defaultPrivacyLevel: "mask-user-input",
    trackResources: true,
    trackLongTasks: true,
    trackUserInteractions: true,
    beforeSend: (event) => {
      // Filter out JWT expired errors (handled by refresh token mechanism)
      if (event.type === "error") {
        const errorMessage = event.error?.message || "";
        if (errorMessage.includes(JWT_EXPIRED_ERROR_MESSAGE)) {
          return false;
        }
      }
      return true;
    },
  });

  datadogRum.startSessionReplayRecording();
};

// Immediate initialization if already authorized
if (currentVendorPermissions.isHeapActive) {
  initHeap();
}

if (
  currentVendorPermissions.isDatadogActive &&
  import.meta.env.VITE_DD_ENV?.toLowerCase() !== "local"
) {
  initializeOrUpdateDatadog();
}

if (import.meta.env.MODE !== "development") {
  const setupAxeptioListeners = (isRetry = false) => {
    try {
      if (window.axeptioSDK) {
        window.axeptioSDK.on("cookies:complete", (choices) => {
          try {
            currentVendorPermissions.isHeapActive = Boolean(choices.heap);
            currentVendorPermissions.isDatadogActive = Boolean(
              choices[DD_RUM_AXEPTIO_KEY]
            );

            if (currentVendorPermissions.isHeapActive) {
              initHeap();
            }
            if (
              currentVendorPermissions.isDatadogActive &&
              import.meta.env.VITE_DD_ENV?.toLowerCase() !== "local"
            ) {
              initializeOrUpdateDatadog();
            }
          } catch (error) {
            console.warn("Axeptio: Failed to process consent choices", error);
          }
        });
        return;
      }
      if (!isRetry) {
        // Retry once after 2 seconds if SDK is not ready
        setTimeout(() => {
          setupAxeptioListeners(true);
        }, 2000);
        return;
      } else {
        console.error("Axeptio SDK failed to load after retry");
      }
    } catch (error) {
      if (!isRetry) {
        setTimeout(() => {
          setupAxeptioListeners(true);
        }, 2000);
        return;
      }
      console.error(
        "Axeptio: Failed to setup event listener after retry",
        error
      );
    }
  };

  setupAxeptioListeners();
}
